import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { redirect } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import type { ID, IRoleEnum } from "@/interfaces";
import { useUiStore } from "@/stores/uiStore";
import type { IUserStore } from "@/stores/userStore/types";
import {
  IAuthStepEnum,
  useAuthStore,
} from "@/templates/authManagement/authStore";
import { routePath } from "@routes/path.ts";
import { APP_ID } from "@/constants/appId";

const name = `${APP_ID}/USER_STORE`;

export const useUserStore = create<IUserStore>()(
  subscribeWithSelector(
    devtools(
      persist(
        (set) => ({
          id: "",
          isAuthenticated: false,
          login: (token: string, role: IRoleEnum | null, id: ID) => {
            set({ isAuthenticated: true, token, role, id });
          },
          logout: () => {
            set({ isAuthenticated: false, token: null, role: null });
            useUiStore.setState({ pageMode: "index" });
            useAuthStore.getState().resetState();
            redirect(
              routePath.authManagement.auth({
                type: IAuthStepEnum.LOGIN?.toString(),
              }),
            );
          },
          role: null,
          token: null,
        }),
        {
          name,
          partialize: (state) => ({
            role: state.role,
            token: state.token,
            id: state.id,
          }),
        },
      ),
      {
        enabled: !import.meta.env.PROD,
        name,
      },
    ),
  ),
);

export const useRole = () => useUserStore(useShallow((state) => state.role));

const initialState = useUserStore.getState();

if (initialState.token) {
  useUserStore.setState({ isAuthenticated: true });
}
